import { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, Inbox } from "lucide-react";
import Logo from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Keep the admin submissions viewer out of search indexes.
    document.title = "Cretexa — Admin";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes?limit=200`);
      setQuotes(data);
    } catch (e) {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white" data-testid="admin-page">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-16">
        <div className="flex items-center justify-between border-b border-[#333] pb-8 mb-10">
          <div>
            <a href="/" className="text-white hover:opacity-80 transition-opacity">
              <Logo markSize={28} textClassName="text-2xl" />
            </a>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#A3A3A3] mt-3">
              Quote Submissions — {quotes.length}
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="inline-flex items-center gap-2 border border-[#333] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:border-[#FF4D00] hover:text-[#FF4D00] transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {loading ? (
          <p className="font-mono text-[#A3A3A3]">Loading…</p>
        ) : quotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-[#555]">
            <Inbox size={48} className="mb-4" />
            <p className="font-mono text-sm uppercase tracking-[0.2em]">No submissions yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q) => (
              <div key={q.id} className="border border-[#333] bg-[#111] p-6 lg:p-8" data-testid={`quote-row-${q.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-4">
                    <span className="font-display font-bold text-2xl">{q.name}</span>
                    <span className="font-mono text-xs uppercase tracking-[0.15em] bg-[#FF4D00] text-black px-3 py-1">
                      {q.project_type}
                    </span>
                  </div>
                  <span className="font-mono text-xs text-[#555]">
                    {new Date(q.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 font-mono text-sm text-[#A3A3A3] mb-5">
                  <div><span className="text-[#555] block text-xs uppercase tracking-[0.15em] mb-1">Email</span>{q.email}</div>
                  <div><span className="text-[#555] block text-xs uppercase tracking-[0.15em] mb-1">Phone</span>{q.phone || "—"}</div>
                  <div><span className="text-[#555] block text-xs uppercase tracking-[0.15em] mb-1">Company</span>{q.company || "—"}</div>
                  <div><span className="text-[#555] block text-xs uppercase tracking-[0.15em] mb-1">Location</span>{q.location || "—"}</div>
                </div>
                <p className="font-mono text-sm text-white leading-relaxed border-t border-[#333] pt-4">{q.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
